import { L } from '@lib';
import { OrderStatus } from '@models';
import { useStore } from '@stores';
import { Button } from 'antd';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { footerButtonStyle } from './footerButtonStyle';
import { NotifyCustomerModal } from './NotifyCustomerModal';

/** What the button does for the job as it stands: see SendForApprovalButton. */
type Mode = 'confirm' | 'send' | 'create' | 'approve';

/** What decides the mode: the job's saved status, whether it is a draft, and where the button is. */
interface Situation {
  status: string;
  isDraft: boolean;
  /** In the Estimates panel's empty list (rather than the footer). */
  inEstimatesPanel: boolean;
}

/**
 * When each mode is offered. The rules do not overlap, so their order does not matter; a job that matches none gets
 * no button.
 */
const RULES: { mode: Mode; applies: (s: Situation) => boolean }[] = [
  // The footer's two steps out of a draft.
  { mode: 'confirm', applies: (s) => !s.inEstimatesPanel && s.isDraft && s.status === OrderStatus.Booked },
  { mode: 'send', applies: (s) => !s.inEstimatesPanel && s.isDraft && s.status === OrderStatus.Approval },
  // An estimate that is out: its way forward is approving it, in the Estimates panel.
  { mode: 'approve', applies: (s) => !s.inEstimatesPanel && !s.isDraft && s.status === OrderStatus.Approval },
  // The panel's empty list: a Booked job (a draft too) can get its first estimate.
  { mode: 'create', applies: (s) => s.inEstimatesPanel && s.status === OrderStatus.Booked },
];

/** What each mode that asks the customer says. */
const MODALS = {
  confirm: {
    button: 'Confirm booking',
    title: 'Confirm booking',
    description:
      'The draft becomes a booking. Do you want to tell the customer it is confirmed? Any changes on the card are saved first.',
    label: 'Tell the customer by',
    okText: 'Confirm booking',
    noneOkText: 'Confirm without notifying',
    sms: 'booking',
  },
  send: {
    button: 'Send estimate',
    title: 'Send estimate to the customer',
    description:
      'The draft becomes an estimate and the customer is asked to approve it. Any changes on the card are saved first.',
    label: 'Send estimate by',
    okText: 'Send estimate',
    noneOkText: 'Send without notifying',
    sms: 'estimate',
  },
  create: {
    button: 'Create estimate',
    title: 'Send estimate to the customer',
    description:
      'The booking becomes an estimate, made from its services as they are now, and the customer is asked to approve it. Any changes on the card are saved first.',
    label: 'Send estimate by',
    okText: 'Send estimate',
    noneOkText: 'Send without notifying',
    sms: 'estimate',
  },
} as const;

/**
 * The step that takes a job out of its draft, by what it is: "Send estimate" turns a draft estimate into an Estimate
 * waiting for the customer; "Confirm booking" turns a draft booking into a Booked one. Either first asks whether, and
 * how, to tell the customer - telling nobody is a fair answer - and only then does it. An estimate that is out with
 * the customer has "Confirm booking" too, but it asks nothing: it opens the Estimates panel, where the estimate is
 * approved. A Booked job that is not a draft has no button in the footer; in the Estimates panel (`fromBooking`, whose
 * list is empty when the button is shown) a Booked job - a draft too - is offered "Create estimate", which turns the
 * booking into an estimate from its current services. Any other job shows nothing. What is on the card is saved first,
 * so the estimate that goes out (a draft's own estimate record is brought up to date) holds the latest services,
 * prices and durations.
 */
export const SendForApprovalButton = observer(
  ({ block, fromBooking, orderId }: { block?: boolean; fromBooking?: boolean; orderId?: string }) => {
    const { orderStore, serviceStore } = useStore();
    const { order } = orderStore;
    const [open, setOpen] = useState(false);

    // The panel names the order it lists: the store's open order may be another one.
    if (!order || (orderId && order.id !== orderId)) return null;

    const status = orderStore.savedStatus ?? order.status;
    const isDraft = !!order.isDraft;

    const situation: Situation = { status, isDraft, inEstimatesPanel: !!fromBooking };
    const mode = RULES.find((rule) => rule.applies(situation))?.mode;

    if (!mode) return null;

    if (mode === 'approve') {
      return (
        <Button type="primary" style={footerButtonStyle(block)} onClick={() => orderStore.requestEstimatesPanel()}>
          {L('Confirm booking')}
        </Button>
      );
    }

    // Saves the card as it is, then does the step; nothing is sent if the save fails.
    const saveThen = (step: () => Promise<boolean>) => async () => {
      if (!(await orderStore.saveCurrentOrder(serviceStore))) return false;
      return step();
    };

    const modal = MODALS[mode];

    return (
      <>
        <Button type="primary" style={footerButtonStyle(block)} onClick={() => setOpen(true)}>
          {L(modal.button)}
        </Button>
        <NotifyCustomerModal
          open={open}
          customer={order.customer}
          title={modal.title}
          description={mode === 'create' && isDraft ? MODALS.send.description : modal.description}
          label={modal.label}
          okText={modal.okText}
          noneOkText={modal.noneOkText}
          sms={modal.sms}
          registration={order.registrationNumber}
          dropOff={mode === 'confirm' ? order.dropOffDate : undefined}
          allowNone
          onConfirm={(channels) =>
            saveThen(() =>
              mode === 'confirm' ? orderStore.confirmBooking(channels) : orderStore.sendForApproval(channels)
            )()
          }
          onCancel={() => setOpen(false)}
        />
      </>
    );
  }
);
